/**
 * StuckQueueCard (BUG-064) — the card shown when queued work needs a
 * decision: moments that couldn't send, or items that couldn't save.
 * One component for both queues so the two surfaces stay twins.
 *
 * Discard is armed-then-confirmed; both buttons keep a fixed width so
 * arming never shifts the layout. Opaque surface on purpose — it lands
 * over the dark header band where glass is unreadable.
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

interface StuckQueueCardProps {
  count: number;
  /** 'moment' (sends) or 'item' (Memories saves). */
  noun: string;
  /** "couldn't send" / "couldn't save". */
  verb: string;
  onRetry: () => void;
  onDiscard: () => void;
  /** Open the send-queue sheet (FEAT-037). "2 moments couldn't send" is a
   * count; the sheet is the one place that says WHICH, and why. */
  onDetails?: () => void;
}

export default function StuckQueueCard({
  count,
  noun,
  verb,
  onRetry,
  onDiscard,
  onDetails,
}: StuckQueueCardProps) {
  const [armed, setArmed] = useState(false);
  const disarmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (disarmTimer.current) clearTimeout(disarmTimer.current);
    };
  }, []);

  const handleDiscard = () => {
    if (!armed) {
      setArmed(true);
      if (disarmTimer.current) clearTimeout(disarmTimer.current);
      disarmTimer.current = setTimeout(() => setArmed(false), 3000);
      return;
    }
    if (disarmTimer.current) clearTimeout(disarmTimer.current);
    setArmed(false);
    onDiscard();
  };

  return (
    <div className="w-full max-w-sm rounded-2xl border border-divider bg-surface p-3 shadow-xl pointer-events-auto animate-fade-in">
      <button
        onClick={onDetails}
        disabled={!onDetails}
        className="flex w-full items-center gap-2.5 px-1 pb-2.5 text-left disabled:cursor-default"
      >
        <AlertTriangle size={18} className="text-warning shrink-0" />
        <span className="text-sm font-semibold text-text">
          {count} {noun}
          {count === 1 ? '' : 's'} couldn&apos;t {verb}
        </span>
      </button>
      <div className="flex gap-2">
        <button
          onClick={onRetry}
          className="min-h-[44px] flex-1 rounded-xl bg-primary text-sm font-semibold text-on-primary transition-transform active:scale-95"
        >
          Try again
        </button>
        <button
          onClick={handleDiscard}
          className={`min-h-[44px] flex-1 rounded-xl border text-sm font-semibold transition-transform active:scale-95 ${
            armed
              ? 'border-error bg-error text-on-primary'
              : 'border-divider text-error'
          }`}
        >
          {armed ? 'Tap to confirm' : 'Discard'}
        </button>
      </div>
    </div>
  );
}
